import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';
import { OrderBonusTier } from './orderPricing';

const AdminOrderBonusConfig: React.FC = () => {
    const [tiers, setTiers] = useState<OrderBonusTier[]>([]);
    const [editing, setEditing] = useState<number | null>(null);
    const [draft, setDraft] = useState({ minSteps: '', maxSteps: '', bonusSteps: '' });
    const [busy, setBusy] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        apiService.get<OrderBonusTier[]>('/orders/bonus-tiers')
            .then((data) => { setTiers(data); setLoaded(true); })
            .catch(() => setError('Không tải được cấu hình vé tặng. Vui lòng tải lại trang.'))
            .finally(() => setBusy(false));
    }, []);

    const reset = () => { setEditing(null); setDraft({ minSteps: '', maxSteps: '', bonusSteps: '' }); };
    const save = async (next: OrderBonusTier[]) => {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            setTiers(await apiService.post<OrderBonusTier[]>('/orders/bonus-tiers', { tiers: next }));
            reset();
            setMessage('Đã lưu cấu hình vé tặng. Cấu hình mới áp dụng khi tạo đơn hoặc đổi số lần mua.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không lưu được cấu hình vé tặng.');
        } finally { setBusy(false); }
    };

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        const tier = { minSteps: Number(draft.minSteps), maxSteps: Number(draft.maxSteps), bonusSteps: Number(draft.bonusSteps) };
        if (!Object.values(tier).every((value) => Number.isSafeInteger(value) && value > 0 && value <= 1000) || tier.minSteps > tier.maxSteps) {
            setError('Nhập số nguyên từ 1 đến 1000; mốc cuối phải từ mốc đầu trở lên.');
            return;
        }
        const next = editing === null ? [...tiers, tier] : tiers.map((item, index) => index === editing ? tier : item);
        const sorted = [...next].sort((a, b) => a.minSteps - b.minSteps);
        if (sorted.some((item, index) => index > 0 && item.minSteps <= sorted[index - 1].maxSteps)) {
            setError('Các khoảng số lần mua không được trùng nhau.');
            return;
        }
        void save(next);
    };

    return <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
        <div><h3 className="font-semibold">Vé tặng theo số lần mua</h3><p className="mt-1 text-sm text-gray-500">Mỗi lần tặng = 38 vé. Áp dụng cả mốc đầu và mốc cuối, mỗi đơn nhận một mức tặng. Tiền đơn tính theo số lần mua.</p></div>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {message && <p role="status" className="text-sm text-green-700">{message}</p>}
        <div className="divide-y">
            {tiers.map((tier, index) => <div key={index} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <span>Mua <strong>{tier.minSteps}–{tier.maxSteps}</strong> lần: tặng <strong>{tier.bonusSteps}</strong> lần = <strong>{(tier.bonusSteps * 38).toLocaleString('vi-VN')} vé</strong></span>
                <div className="flex gap-2">
                    <button type="button" disabled={busy} onClick={() => { setEditing(index); setDraft({ minSteps: String(tier.minSteps), maxSteps: String(tier.maxSteps), bonusSteps: String(tier.bonusSteps) }); }} className="rounded-lg border px-3 py-2 disabled:opacity-50">Sửa</button>
                    <button type="button" disabled={busy} onClick={() => void save(tiers.filter((_, current) => current !== index))} className="rounded-lg border border-red-200 px-3 py-2 text-red-700 disabled:opacity-50">Xoá</button>
                </div>
            </div>)}
            {loaded && tiers.length === 0 && <p className="py-3 text-sm text-gray-500">Chưa có mốc vé tặng. Đơn mới không được cộng thêm vé.</p>}
        </div>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3 border-t pt-4">
            {([['minSteps', 'Mua từ (lần)'], ['maxSteps', 'Đến (lần)'], ['bonusSteps', 'Tặng thêm (lần)']] as const).map(([key, label]) => <label key={key} className="flex min-w-0 flex-1 flex-col gap-1 text-sm">{label}<input required type="number" min={1} max={1000} step={1} value={draft[key]} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))} className="w-full rounded-lg border px-3 py-2" /></label>)}
            <button disabled={busy || !loaded} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Đang tải/lưu...' : editing === null ? 'Thêm mốc' : 'Lưu sửa'}</button>
            {editing !== null && <button type="button" onClick={reset} disabled={busy} className="rounded-lg border px-3 py-2 text-sm">Huỷ sửa</button>}
        </form>
    </section>;
};

export default AdminOrderBonusConfig;
